/* ============================================================
   ÉKO '77 — the menu page's sticky section rail

   Reference: the IIFE at the foot of eko77-redesign.pages.dev/menu/ —
   measure the two fixed bars, publish their heights, spy on the scroll
   position and keep the live chip inside the rail.

   THREE JOBS.

   1. BUILD THE RAIL FROM THE PAGE.
      The reference hardcodes nine links. Here the courses are nine
      instances of sections/x-menu-course.liquid in templates/page.menu.json,
      and Liquid cannot enumerate its sibling sections — a section only ever
      sees its own settings. So the rail is built from what those sections
      actually rendered: every .x-menu-course__body[id] on the page becomes a
      chip labelled with its own .x-menu-course__name. Add, rename, reorder
      or delete a course in the theme editor and the rail follows on the next
      shopify:section:* event, with no second list to keep in step.
      A merchant who wants to point somewhere else entirely switches the
      section to "Custom links" and the blocks are rendered server-side
      instead; everything below then drives those, unchanged.

   2. PUBLISH --nav-h.
      The bar is sticky, so anything that has to clear it needs its height:
      assets/x-menu-course.css sets
        scroll-margin-top: calc(var(--head-h, 62px) + var(--nav-h, 50px) + 12px)
      on every anchored course. The section emits a pre-JS value from the
      merchant's padding setting; this file replaces it with the measured
      one and a ResizeObserver keeps it honest when the viewport, the fonts
      or those settings change. --head-h belongs to the header
      (assets/x-site-head.js) and is only ever read here.

   3. SPY, AND CENTRE.
      The active chip is the last course whose top has passed a line just
      under the two bars — the reference's rule, with its own `.menu-close`
      special case generalised to "the page is scrolled to the bottom", so it
      keeps working before sections/x-menu-close lands and after it does.
      Bringing that chip into view sets the RAIL's scrollLeft rather than
      calling scrollIntoView(), which would also scroll the PAGE and fight
      the sticky bar — the same reason assets/x-chips.js gives.

   NOT DONE HERE, DELIBERATELY: the click itself. `href="#starters"` is a
   real link, the browser jumps natively and the scroll-margin-top above
   lands it clear of both bars — so the rail works with scripting off apart
   from the highlight. Smooth scrolling is opt-in (a setting), because the
   reference jumps.
   ============================================================ */

(function () {
  'use strict';

  var TARGETS = '.x-menu-course__body[id]';
  var LABELS = '.x-menu-course__name';
  var HEAD_FALLBACK = 62;
  var EDGE = 2;

  function num(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  /* The header owns --head-h and measures itself. Reading the property is
     what keeps this rail's line identical to its own sticky `top`, which is
     written from the same property. */
  function headHeight() {
    var declared = num(
      getComputedStyle(document.documentElement).getPropertyValue('--head-h'),
      NaN
    );
    if (!isNaN(declared)) return declared;

    var bar = document.querySelector('[data-x-head-bar]');
    if (bar) return Math.round(bar.getBoundingClientRect().height);
    return HEAD_FALLBACK;
  }

  class XMenuSecnav extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-x-secnav-rail]');
      if (!this.rail) return;

      this.manual = this.dataset.source === 'manual';
      this.sticky = this.dataset.sticky === 'true';
      this.smooth = this.dataset.smooth === 'true';
      this.spyOffset = num(this.dataset.spyOffset, 14);
      this.reduced =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.links = [];
      this.targets = [];
      this.signature = '';
      this.current = -1;
      this.navHeight = 0;
      this.ticking = false;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSectionChange = this.onSectionChange.bind(this);
      this.measure = this.measure.bind(this);
      this.spy = this.spy.bind(this);

      this.build();
      this.measure();
      this.watchSize();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.rail.addEventListener('click', this.onClick);
      document.addEventListener('shopify:section:load', this.onSectionChange);
      document.addEventListener('shopify:section:unload', this.onSectionChange);
      document.addEventListener('shopify:section:reorder', this.onSectionChange);

      this.spy();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.rail) this.rail.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:section:load', this.onSectionChange);
      document.removeEventListener('shopify:section:unload', this.onSectionChange);
      document.removeEventListener('shopify:section:reorder', this.onSectionChange);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      /* the courses' scroll-margin must stop reserving room for a bar that
         is no longer on the page */
      document.documentElement.style.setProperty('--nav-h', '0px');
    }

    /* ---------------- 1. the rail ---------------- */

    courses() {
      var found = [];
      var nodes = document.querySelectorAll(TARGETS);
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        if (this.contains(node)) continue;
        var label = node.querySelector(LABELS);
        found.push({
          id: node.id,
          label: (label ? label.textContent : '').trim() || node.id,
          node: node
        });
      }
      return found;
    }

    build() {
      if (this.manual) {
        this.links = Array.prototype.slice.call(
          this.rail.querySelectorAll('[data-x-secnav-link]')
        );
        this.targets = this.links.map(function (a) {
          var href = a.getAttribute('href') || '';
          var id = href.charAt(0) === '#' ? href.slice(1) : '';
          return id ? document.getElementById(id) : null;
        });
        this.setAttribute('data-empty', this.links.length ? 'false' : 'true');
        return;
      }

      var courses = this.courses();
      var signature = courses
        .map(function (c) {
          return c.id + ' ' + c.label;
        })
        .join('');

      /* The theme editor re-renders a section on every keystroke. Rewriting
         an identical rail would restart the underline transition and throw
         away the rail's own scroll position, so it is only rebuilt when the
         courses genuinely changed. */
      if (signature !== this.signature) {
        this.signature = signature;
        var fragment = document.createDocumentFragment();
        for (var i = 0; i < courses.length; i++) {
          var a = document.createElement('a');
          a.className = 'x-secnav__link';
          a.setAttribute('data-x-secnav-link', '');
          a.href = '#' + courses[i].id;
          a.textContent = courses[i].label;
          fragment.appendChild(a);
        }
        this.rail.textContent = '';
        this.rail.appendChild(fragment);
        this.current = -1;
      }

      this.links = Array.prototype.slice.call(
        this.rail.querySelectorAll('[data-x-secnav-link]')
      );
      this.targets = courses.map(function (c) {
        return c.node;
      });
      this.setAttribute('data-empty', courses.length ? 'false' : 'true');
    }

    /* ---------------- 2. --nav-h ---------------- */

    measure() {
      var h = this.sticky ? Math.round(this.getBoundingClientRect().height) : 0;
      if (h === this.navHeight) return;
      this.navHeight = h;
      document.documentElement.style.setProperty('--nav-h', h + 'px');
    }

    watchSize() {
      if (!('ResizeObserver' in window)) {
        window.addEventListener('resize', this.measure, { passive: true });
        return;
      }
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this);
    }

    /* ---------------- 3. the spy ---------------- */

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      var self = this;
      requestAnimationFrame(function () {
        self.ticking = false;
        self.spy();
      });
    }

    onResize() {
      this.measure();
      this.current = -1;
      this.onScroll();
    }

    onSectionChange() {
      this.build();
      this.measure();
      this.current = -1;
      this.spy();
    }

    spy() {
      if (!this.links.length) return;

      var line = headHeight() + this.navHeight + this.spyOffset;
      var index = 0;
      for (var k = 0; k < this.targets.length; k++) {
        var target = this.targets[k];
        if (target && target.getBoundingClientRect().top <= line) index = k;
      }

      /* Past the last course — a tall closing section can keep its top below
         the line for ever, and the rail must not still be pointing at Sides. */
      var doc = document.documentElement;
      if (window.innerHeight + window.scrollY >= doc.scrollHeight - EDGE) {
        index = this.targets.length - 1;
      }

      if (index === this.current) return;
      this.current = index;

      for (var i = 0; i < this.links.length; i++) {
        var link = this.links[i];
        var on = i === index;
        link.classList.toggle('is-on', on);
        if (on) {
          link.setAttribute('aria-current', 'true');
          this.centre(link);
        } else {
          link.removeAttribute('aria-current');
        }
      }
    }

    /* keep the live chip in view without ever nudging the page itself */
    centre(link) {
      var max = this.rail.scrollWidth - this.rail.clientWidth;
      if (max <= EDGE) return;

      var offset =
        link.getBoundingClientRect().left -
        this.rail.getBoundingClientRect().left +
        this.rail.scrollLeft;
      var want = offset - (this.rail.clientWidth - link.offsetWidth) / 2;
      var left = Math.max(0, Math.min(want, max));

      if (this.rail.scrollTo) {
        this.rail.scrollTo({
          left: left,
          behavior: this.reduced ? 'auto' : 'smooth'
        });
      } else {
        this.rail.scrollLeft = left;
      }
    }

    /* ---------------- the click ---------------- */

    onClick(event) {
      var link = event.target.closest('[data-x-secnav-link]');
      if (!link) return;

      var index = this.links.indexOf(link);
      if (index > -1) {
        /* paint the state now rather than waiting for the scroll to settle */
        this.current = index;
        for (var i = 0; i < this.links.length; i++) {
          var on = i === index;
          this.links[i].classList.toggle('is-on', on);
          if (on) this.links[i].setAttribute('aria-current', 'true');
          else this.links[i].removeAttribute('aria-current');
        }
        this.centre(link);
      }

      if (!this.smooth || this.reduced) return;

      var href = link.getAttribute('href') || '';
      var target = href.charAt(0) === '#' ? document.getElementById(href.slice(1)) : null;
      if (!target || !target.scrollIntoView) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      /* the URL still has to name the course, without a second history entry */
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', href);
      }
    }
  }

  if (!customElements.get('x-menu-secnav')) {
    customElements.define('x-menu-secnav', XMenuSecnav);
  }
})();
